import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import Logo from '../assets/Logo';
import { useAppContext } from '../hooks/AppProvider';

const Container = styled('div')({
  position: 'relative',
});

const animationProps = new Set(['menuOpen', 'reducedMotion']);
const shouldForwardAnimationProp = (prop) => !animationProps.has(prop);

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: shouldForwardAnimationProp,
})(({ theme }) => ({
  fontSize: '1.75rem',
  zIndex: 3,
  [theme.breakpoints.up('md')]: {
    fontSize: '3rem',
  },
}));

const MenuGlyph = styled('span', {
  shouldForwardProp: shouldForwardAnimationProp,
})(({ menuOpen, reducedMotion }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.25em',
  height: '1.25em',
  '.menu-line': {
    position: 'absolute',
    width: '0.9em',
    height: '0.12em',
    borderRadius: '999px',
    backgroundColor: 'currentColor',
    transition: reducedMotion
      ? 'none'
      : 'transform 0.28s ease, opacity 0.2s ease, width 0.28s ease',
  },
  '.menu-line-top': {
    transform: menuOpen
      ? 'translateY(0) rotate(45deg)'
      : 'translateY(-0.26em) rotate(0deg)',
  },
  '.menu-line-middle': {
    opacity: menuOpen ? 0 : 1,
    transform: menuOpen ? 'scaleX(0.35)' : 'scaleX(1)',
  },
  '.menu-line-bottom': {
    transform: menuOpen
      ? 'translateY(0) rotate(-45deg)'
      : 'translateY(0.26em) rotate(0deg)',
  },
}));

const StyledMenu = styled('div', {
  shouldForwardProp: shouldForwardAnimationProp,
})(({ menuOpen, reducedMotion, theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100vw',
  height: '100vh',
  minHeight: '100dvh',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  backgroundColor: theme.palette.background.paper,
  clipPath: menuOpen
    ? 'circle(150vmax at var(--menu-origin-x) var(--menu-origin-y))'
    : 'circle(0 at var(--menu-origin-x) var(--menu-origin-y))',
  fontSize: '3rem',
  pointerEvents: menuOpen ? 'auto' : 'none',
  visibility: menuOpen ? 'visible' : 'hidden',
  zIndex: 2,
  transition: reducedMotion
    ? 'none'
    : menuOpen
      ? 'clip-path 0.75s ease-in-out, visibility 0s'
      : 'clip-path 0.5s ease-in-out, visibility 0s 0.5s',
  nav: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    minHeight: '100%',
    padding: '5rem 1rem 2rem',
    width: '100%',
  },
  'nav > a': {
    opacity: menuOpen ? 1 : 0,
    pointerEvents: menuOpen ? 'auto' : 'none',
    transform: menuOpen ? 'translateY(0)' : 'translateY(50px)',
    transition: reducedMotion
      ? 'none'
      : 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out',
  },
  ...Array.from({ length: 6 }, (_, index) => ({
    [`nav > a:nth-of-type(${index + 1})`]: {
      transitionDelay: menuOpen && !reducedMotion ? `${0.2 + index * 0.05}s` : '0s',
    },
  })).reduce((styles, item) => ({ ...styles, ...item }), {}),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  fontFamily: 'Usuzi',
  fontSize: 'max(2vw, 16pt)',
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  textDecoration: 'none',
  '&:hover, &:focus-visible': {
    color: theme.palette.brand.focus,
  },
}));

const HamburgerIcon = ({ menuOpen = false, reducedMotion = false }) => (
  <MenuGlyph aria-hidden="true" menuOpen={menuOpen} reducedMotion={reducedMotion}>
    <span className="menu-line menu-line-top" />
    <span className="menu-line menu-line-middle" />
    <span className="menu-line menu-line-bottom" />
  </MenuGlyph>
);

HamburgerIcon.propTypes = {
  menuOpen: PropTypes.bool,
  reducedMotion: PropTypes.bool,
};

const Menu = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuOrigin, setMenuOrigin] = React.useState({ x: '100%', y: '0px' });
  const menuButton = React.useRef(null);
  const menuPanel = React.useRef(null);
  const { reducedMotion } = useAppContext();
  const { pathname } = useLocation();

  const toggleMenu = () => {
    if (!menuOpen) {
      const { top, left, width, height } = menuButton.current.getBoundingClientRect();
      setMenuOrigin({
        x: `${left + width / 2}px`,
        y: `${top + height / 2}px`,
      });
    }
    setMenuOpen((open) => !open);
  };

  const closeMenu = () => setMenuOpen(false);

  React.useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const backgroundElements = document.querySelectorAll('main, footer');
    const previousOverflow = document.body.style.overflow;
    const links = Array.from(menuPanel.current.querySelectorAll('a'));
    const focusableElements = [menuButton.current, ...links];

    document.body.style.overflow = 'hidden';
    backgroundElements.forEach((element) => {
      element.inert = true;
    });
    links[0]?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      backgroundElements.forEach((element) => {
        element.inert = false;
      });
      menuButton.current?.focus();
    };
  }, [menuOpen]);

  return (
    <Container>
      <Tooltip title={menuOpen ? 'Menü schließen' : 'Menü öffnen'}>
        <StyledIconButton
          ref={menuButton}
          onClick={toggleMenu}
          color="inherit"
          className="menu-button borderBox"
          menuOpen={menuOpen}
          reducedMotion={reducedMotion}
          aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
          aria-controls="mainmenu"
          aria-expanded={menuOpen}
        >
          <HamburgerIcon menuOpen={menuOpen} reducedMotion={reducedMotion} />
        </StyledIconButton>
      </Tooltip>
      <StyledMenu
        ref={menuPanel}
        id="mainmenu"
        aria-hidden={!menuOpen}
        menuOpen={menuOpen}
        reducedMotion={reducedMotion}
        style={{
          '--menu-origin-x': menuOrigin.x,
          '--menu-origin-y': menuOrigin.y,
        }}
      >
        <nav aria-label="Hauptnavigation">
          <StyledLink aria-current={pathname === '/' ? 'page' : undefined} aria-label="Startseite" onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/"><Logo /></StyledLink>
          <StyledLink aria-current={pathname === '/administration' ? 'page' : undefined} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/administration">Administration</StyledLink>
          <StyledLink aria-current={pathname === '/software' ? 'page' : undefined} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/software">Software</StyledLink>
          <StyledLink aria-current={pathname === '/hosting' ? 'page' : undefined} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/hosting">Hosting</StyledLink>
          <StyledLink aria-current={pathname === '/impressum' ? 'page' : undefined} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/impressum">Impressum</StyledLink>
          <StyledLink aria-current={pathname === '/datenschutz' ? 'page' : undefined} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1} to="/datenschutz">Datenschutz</StyledLink>
        </nav>
      </StyledMenu>
    </Container>
  );
};

export default Menu;
